import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Header from '../components/Header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { itineraryApi } from '../services/api';
import { saveTripsOffline, getOfflineTrips } from '../utils/offlineDb';
import { Loader2 } from 'lucide-react';
import { GuidedTour, TourHelpButton } from '../components/GuidedTour';
import { DashboardHeader, OfflineDataBanner } from '../components/dashboard/DashboardHeader';
import { TripCard } from '../components/dashboard/TripCard';
import { EmptyState } from '../components/dashboard/EmptyState';

const DashboardPage = () => {
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showTour, setShowTour] = useState(false);

  const fetchItineraries = useCallback(async () => {
    try {
      const response = await itineraryApi.list();
      setItineraries(response.data);
      setIsOfflineData(false);
      saveTripsOffline(response.data);
    } catch {
      const { trips, cachedAt: cached } = await getOfflineTrips();
      if (trips.length > 0) {
        setItineraries(trips);
        setIsOfflineData(true);
        setCachedAt(cached);
      } else {
        toast.error('Failed to load itineraries');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItineraries();
    if (!localStorage.getItem('triplea_tour_completed')) {
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [fetchItineraries]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchItineraries();
    setRefreshing(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await itineraryApi.delete(deleteId);
      setItineraries(itineraries.filter((it) => it.id !== deleteId));
      toast.success('Itinerary deleted');
    } catch {
      toast.error('Failed to delete itinerary');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="dashboard-page">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='1' d='M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
          backgroundSize: '100% auto',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat'
        }} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-7xl mx-auto">
          <DashboardHeader
            count={itineraries.length}
            isOfflineData={isOfflineData}
            refreshing={refreshing}
            onRefresh={handleRefresh}
          />

          {isOfflineData && <OfflineDataBanner cachedAt={cachedAt} />}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl">
                <Loader2 className="w-8 h-8 animate-spin text-ocean" />
              </div>
            </div>
          ) : itineraries.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {itineraries.map((itinerary, index) => (
                <TripCard
                  key={itinerary.id}
                  itinerary={itinerary}
                  index={index}
                  onDelete={setDeleteId}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Itinerary?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The itinerary will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showTour && <GuidedTour onComplete={() => setShowTour(false)} />}
      {!showTour && <TourHelpButton onClick={() => setShowTour(true)} />}
    </div>
  );
};

export default DashboardPage;
